import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { DATE_FORMAT_MOMENT } from "constants/common";
import styled from "styled-components";
import SkedButton from "ui/SkedButton/SkedButton";
import SkedIcon from "ui/SkedIcon/SkedIcon";
import FormControl from "ui/FormControl/FormControl";
import { DeprecatedLink } from "ui/Link";
import Moment from "moment";
import "moment-timezone";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import { getUser } from "selectors/commonSelectors";

// Embedded (in-dialog) presentation. The popover styles the whole calendar in
// brand purple, which is far too loud when the picker is the body of a dialog —
// here the days read as neutral text and purple is reserved for the selection.
const EMBEDDED_STYLES = `
  .sked-datetime-picker--embedded .DayPicker {
    font-size: 0.875rem;
  }

  .sked-datetime-picker--embedded .DayPicker-Caption > div {
    color: #333333;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .sked-datetime-picker--embedded .DayPicker-NavButton {
    color: #6b7280;
  }

  .sked-datetime-picker--embedded .DayPicker-Weekday {
    color: #9ca3af;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .sked-datetime-picker--embedded .DayPicker-Day {
    border-radius: 0.375rem;
    color: #333333;
    font-weight: 500;
    transition: background-color 120ms ease, color 120ms ease;
  }

  .sked-datetime-picker--embedded .DayPicker-Day--outside,
  .sked-datetime-picker--embedded .DayPicker-Day--disabled {
    color: #c4c4c4;
  }

  .sked-datetime-picker--embedded
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):hover {
    background: #f4f3ff;
    color: #4f31ff;
  }

  /* The hour/minute fields default to a grey fill that reads as disabled next
     to the lavender meridian chip. */
  .sked-datetime-picker--embedded input.TimeToggle {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    color: #333333;
    font-weight: 600;
  }

  .sked-datetime-picker--embedded input.TimeToggle:focus {
    border-color: #4f31ff;
    outline: none;
  }
`;

const DateTimePicker = ({
  showTriangle = true,
  showControls = true,
  blockPastDates = true,
  blockFutureDates = false,
  className = "",
  selectedDate,
  timePreference = "12h",
  onChange,
  onClose,
  arrowSide = "right",
  maxSelectableDate,
  minSelectableDate = "",
  showTimePicker = true,
  // Renders the picker as part of the surrounding surface (e.g. inside a dialog)
  // rather than as a floating popover: no shadow, no fixed min-width, one ground.
  embedded = false,
}) => {
  const user = useSelector(getUser);
  // Moment.tz(undefined) returns undefined, so an unset timezone turns every
  // downstream .toDate() into a TypeError that takes the whole picker down.
  const timezone = user?.timezone || Moment.tz.guess() || "UTC";
  const defaultDate = useMemo(() => {
    return selectedDate && Moment(selectedDate).isValid()
      ? selectedDate
      : new Moment().format(DATE_FORMAT_MOMENT);
  }, []);
  const [hasValidDate, setHasValidDate] = useState(
    () => !selectedDate || Moment(selectedDate).isValid()
  );
  const [dateTime, setDateTime] = useState(defaultDate);
  const [date, setDate] = useState(defaultDate);
  // always pass in time as 24h time. The format is handled
  const [time, setTime] = useState(new Moment(defaultDate).format("HH:mm"));
  const node = useRef();
  const hasValidTime = Moment(time, ["HH:mm", "HH:mm:ss"], true).isValid();

  const handleSetDateTime = () => {
    if (!hasValidDate || (showTimePicker && !hasValidTime)) return;
    const datePart = new Moment(date).format("YYYY-MM-DD");
    if (!showTimePicker) {
      if (onChange) onChange(datePart);
      return;
    }

    // sometimes picker returns single digit hours/minutes and moment.format fails. THis hack fixes it
    const formattedTime = time
      .split(":")
      .map((field) => field.padStart(2, "0"))
      .join(":");
    const newDate = `${datePart} ${formattedTime}`;
    if (onChange) onChange(newDate);
  };

  useEffect(() => {
    // fire onchange event if controls are disabled
    if (!showControls) handleSetDateTime();
  }, [date, time, hasValidDate]);

  const disabledDays = {};
  const today = new Moment().tz(timezone).toDate();
  const minSelectableDates = [
    blockPastDates ? new Moment(today) : null,
    minSelectableDate ? Moment.tz(minSelectableDate, timezone) : null,
  ].filter(Boolean);
  const minDate =
    minSelectableDates.length > 0
      ? Moment.max(minSelectableDates).toDate()
      : null;
  if (minDate) disabledDays.before = minDate;
  if (blockFutureDates) disabledDays.after = today;
  if (maxSelectableDate) disabledDays.after = maxSelectableDate;

  return (
    <div
      ref={node}
      className={`relative ${embedded ? "sked-datetime-picker--embedded" : ""} ${className}`}
    >
      {embedded && <style>{EMBEDDED_STYLES}</style>}
      <div
        className={`${
          showTimePicker
            ? `flex ${embedded ? "w-full" : "min-w-400px"}`
            : "inline-block"
        } text-blue-500 border border-solid rounded-lg bg-white ${
          embedded
            ? "border-gray-100"
            : "border-gray-500 overflow-hidden shadow-lg"
        }`}
      >
        <DayPicker
          showOutsideDays
          className={`p-2 ${embedded ? "bg-white" : "bg-blue-25"}`}
          initialMonth={new Moment(date).toDate()}
          selectedDays={hasValidDate ? [new Moment(date).toDate()] : []}
          disabledDays={[disabledDays]}
          onDayClick={(day) => {
            // cancel date selection if date is blocked
            const isBefore = minDate
              ? new Moment(day).isBefore(
                  new Moment(minDate).tz(timezone).format("YYYY-MM-DD")
                )
              : false;
            const isAfter = new Moment(day).isAfter(
              new Moment().tz(timezone).format("YYYY-MM-DD")
            );
            if ((isBefore && minDate) || (isAfter && blockFutureDates)) {
              return;
            }
            if (
              maxSelectableDate &&
              new Moment(day).isAfter(maxSelectableDate)
            ) {
              return;
            }

            setDate(day);
            setHasValidDate(true);
          }}
          modifiersStyles={{
            selected: {
              color: "white",
              background: "#4F31FF",
              borderRadius: "5px",
            },
            caption: {
              color: "#b3b3b3",
            },
          }}
        />
        <div
          className={`flex flex-col text-center w-full bg-white ${
            showTimePicker ? "pt-4" : "pt-0"
          } ${
            embedded
              ? "px-4 pb-4 border-0 border-l border-solid border-gray-100 justify-center"
              : "px-2 pb-2"
          }`}
        >
          {!hasValidDate && (
            <p role="alert" className="text-sm text-red-500 p-2">
              Choose a date to replace the invalid schedule.
            </p>
          )}
          {showTimePicker && (
            <>
              <div
                className={
                  embedded
                    ? "text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3"
                    : "text-base text-gray-700"
                }
              >
                Posting Time
              </div>
              <TimePicker
                initialDateTime={new Moment(dateTime)}
                timePreference={timePreference}
                onChange={(newTime) => setTime(newTime)}
                onInvalidTime={() => setTime("")}
                // Embedded keeps the steppers tucked under their label; the
                // popover stretches them to fill its fixed-height column.
                className={`w-full justify-center ${embedded ? "" : "flex-grow"}`}
              />
            </>
          )}
          {showControls && (
            <div className="flex items-center justify-center pb-2 px-2">
              <DeprecatedLink
                type="neutral"
                onClick={() => (onClose ? onClose() : null)}
              >
                Cancel
              </DeprecatedLink>
              <div className="flex-grow flex justify-end">
                <SkedButton
                  size="small"
                  type="primary"
                  className="bg-white"
                  disabled={!hasValidDate || (showTimePicker && !hasValidTime)}
                  onClick={() => handleSetDateTime()}
                >
                  Set
                </SkedButton>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Outer Triangle (border) */}
      {showTriangle && (
        <>
          <div
            className="absolute"
            style={{
              bottom: "-19px",
              [arrowSide]: "75px",
              zIndex: "1001",
              width: 0,
              height: 0,
              borderLeft: "20px solid transparent",
              borderRight: "20px solid transparent",
              borderTop: `20px solid #b3b3b3`,
            }}
          >
            &nbsp;
          </div>
          {/* Inner triangle (fill) */}
          <div
            className="absolute"
            style={{
              bottom: "-17px",
              [arrowSide]: "76px",
              zIndex: "1002",
              width: 0,
              height: 0,
              borderLeft: "19px solid transparent",
              borderRight: "19px solid transparent",
              borderTop: `19px solid ${
                arrowSide == "right" ? "white" : "#F6F5FF"
              }`,
            }}
          >
            &nbsp;
          </div>
        </>
      )}
    </div>
  );
};

const TimePickerComponent = ({
  initialDateTime,
  timePreference,
  className = "",
  onChange,
  onInvalidTime = () => {},
  minutesPickerStep = 1,
}) => {
  let using12h = timePreference === "12h";
  const [hour, setHour] = useState(initialDateTime.format("HH"));
  const [minute, setMinute] = useState(initialDateTime.format("mm"));
  const [meridian, setMeridian] = useState(initialDateTime.format("A"));

  // allow maxHr to be 24, at 24 the code below resets the time to 00
  const maxHr = using12h ? 12 : 24;

  const handleTimeChange = (
    newHour,
    newMinute,
    changeMeridian = false,
    oldHour,
    oldMinute
  ) => {
    if (newHour === 24 && oldHour === 23) {
      // if the hour changing to is 24 from 23, swap to 00
      // otherwise when you click up from 23 it will skip to 01
      newHour = 0;
    } else if (newHour === 24) {
      // user has gone from 00 'down' so this should be 23
      newHour = 23;
    }
    if (newMinute === 60 && oldMinute === 60 - minutesPickerStep) {
      // if the minute is changing to 60 from 59 or 55 (depending on minutesPickerStep), reset to 00
      newMinute = 0;
    } else if (newMinute === 60) {
      // the user is doing from 00 'down' -> should go to 59 or 55 (depending on minutesPickerStep)
      newMinute = 60 - minutesPickerStep;
    }
    // Don't store non-finite values in state; let empty strings remain as a local editing state.
    if (
      !Number.isFinite(Number(newHour)) ||
      !Number.isFinite(Number(newMinute))
    ) {
      onInvalidTime();
      return;
    }

    if (newHour !== hour) {
      setHour(newHour);
    }

    if (newMinute !== minute) {
      setMinute(newMinute);
    }

    // Clearing an input is a valid editing state, but not a publishing time.
    if (newHour === "" || newMinute === "") {
      onInvalidTime();
      return;
    }

    let newMeridian = meridian;
    if (changeMeridian) {
      newMeridian = newMeridian === "PM" ? "AM" : "PM";
      setMeridian(newMeridian);
    }

    let hour24 = parseInt(newHour);
    if (using12h && newHour < 12 && newMeridian === "PM") {
      hour24 += 12;
    } else if (using12h && newHour >= 12 && newMeridian === "AM") {
      hour24 -= 12;
    }
    // shim this because Moment expects the hour to always be 2 digits
    // bug is seen on Safari otherwise
    if (hour24 < 10) {
      hour24 = "0" + hour24;
    }
    const time = `${hour24}:${String(newMinute).padStart(2, "0")}:00`;

    onChange ? onChange(time) : null;
  };

  return (
    <div className={`flex items-center ${className}`}>
      <TimeToggle
        value={hour}
        displayValue={using12h ? (hour > 12 ? hour % 12 : hour) : hour}
        min={using12h ? 1 : 0}
        max={maxHr}
        onChange={(newValue, overflow) =>
          handleTimeChange(newValue, minute, overflow, hour, minute)
        }
        className="mr-2"
      />
      :
      <TimeToggle
        value={minute}
        min={0}
        max={60}
        step={minutesPickerStep}
        className="ml-2"
        onChange={(newValue) =>
          handleTimeChange(hour, newValue, false, hour, minute)
        }
      />
      {using12h && (
        <Meridian
          className={`ml-2 p-2 rounded-lg bg-blue-100 cursor-pointer`}
          onClick={() => handleTimeChange(hour, minute, true)}
        >
          {meridian}
        </Meridian>
      )}
    </div>
  );
};

const Meridian = styled.div`
  width: 44px;
  font-family: monospace;
  color: #555;
  text-align: center;
  user-select: none;
`;

export const TimePicker = styled(TimePickerComponent)``;

const TimeToggle = ({
  value,
  displayValue = null,
  min,
  max,
  step = 1,
  className = "",
  onChange,
}) => {
  if (displayValue === null) {
    displayValue = value;
  }

  displayValue = displayValue.toString();
  if (displayValue.length === 1) {
    displayValue = `0${displayValue}`;
  }

  const handleUp = () => {
    let newVal = parseInt(value) + step;

    let changeMeridian = false;
    if (newVal === max || newVal === min) {
      changeMeridian = true;
    }

    if (newVal > max) {
      newVal %= max;
    }

    onChange ? onChange(newVal, changeMeridian) : null;
  };

  const handleDown = () => {
    let newVal = parseInt(value) - step;
    let changeMeridian = false;
    if (newVal < min) {
      newVal = max;
    }
    // current value is maximum (12, will not happen in 24h time) and user hits down
    // 12 -> 11 should change meridian (12pm -> 11am)
    // 01 -> 12 should NOT change meridian (midnight ->
    // but 01 -> 12 should not
    if (value === max && newVal === 11) {
      changeMeridian = true;
    }
    // was 1, going to 12 - should not change meridian

    onChange ? onChange(newVal, changeMeridian) : null;
  };

  const handleChange = (e) => {
    let val = e.target.value;
    try {
      if (val !== "") {
        val = parseInt(val);
        if (val > max || val < min || Number.isNaN(val)) {
          return;
        }
      }

      onChange ? onChange(val) : null;
    } catch {
      //noop
    }
  };

  return (
    <div className={`flex flex-col text-center ${className}`}>
      <div>
        <SkedIcon
          icon="icon-up-close"
          color="purple"
          size="20"
          className="font-bold"
          onClick={() => handleUp(displayValue)}
        />
      </div>
      <div className="my-1">
        <FormControl
          type="text"
          value={displayValue}
          className="TimeToggle w-40px p-0 text-center appearance-none"
          onChange={(e) => handleChange(e)}
        />
      </div>
      <div>
        <SkedIcon
          icon="icon-down-close"
          color="purple"
          size="20"
          className="font-bold"
          onClick={() => handleDown(displayValue)}
        />
      </div>
    </div>
  );
};

export default DateTimePicker;
